import type { DocNode } from '@atlaskit/adf-schema/doc';

import type { FieldId, OptionId } from '../../common/types/form/form-fields';
import type { IsoDate, LocalTime } from '../../common/types/form/form-fields/field-values';
import type {
	FormSectionConditionCheckGroup,
	FormSectionConditionOperator,
} from '../../common/types/form/form-section';

/**
 * A Proforma form with everything: supports all features in Proforma.
 */
export interface ProformaForm {
	/**
	 * ID of this form in its context. A 'context' means a Jira issue or a Jira project or perhaps something else.
	 * IDs are not necessarily unique across all of Proforma, only unique within their context.
	 */
	id: number;

	/**
	 * UUID of this form in its context. A 'context' means a Jira issue or a Jira project or perhaps something else.
	 * Currently, UUIDs are optional and are only available for newly generated and migrated forms. Eventually this will take
	 * over as the identifiers.
	 */
	uuid: string;

	/**
	 * Restriction status applied on submitted forms.
	 * Proforma represents no restrictions as a missing field instead of an explicit 'open' status.
	 */
	restrictionStatus?: Exclude<FormRestrictionStatus, 'open'>;

	/**
	 * The date this form was last updated. Should be formatted as an ISO 8601 date and time in the UTC timezone (Z).
	 */
	updated: UtcDateTime;

	/**
	 * Optional property that configures how this form is published (if it is published somewhere).
	 * Used only on template forms, not available on forms.
	 */
	publish?: FormPublishing;

	/**
	 * The design of the form: the static parts that don't change when filled in such as questions, sections and layout.
	 */
	design: FormDesign;

	/**
	 * The state of the form: the dynamic parts that people change when filling in a form.
	 * Optional property, it is not available on template forms.
	 */
	state?: FormState;
}

/**
 * A Proforma form that cannot be filled out.  It serves as a template for forms that can be filled out, and is typically
 * copied onto Jira issues where it can be filled out.
 */
export interface TemplateForm extends ProformaForm {
	/**
	 * Template forms do not have a state because they cannot be filled out.
	 */
	state: undefined;
}

/**
 * A Proforma form that can be filled out.
 */
export interface Form extends ProformaForm {
	/**
	 * The state of the form: the dynamic parts that people change when filling in a form.
	 */
	state: FormState;
}

/**
 * Configures how this form is published (if it is published somewhere).
 * Used only on template forms, not available on forms.
 */
export interface FormPublishing {
	jira?: FormPublishingJira;
	portal?: FormPublishingPortal;
}

/**
 * Configures how this form is published within Jira.
 * Used only on template forms, not available on forms.
 */
export interface FormPublishingJira {
	/**
	 * The IDs of request types or issue types that this form is recommended for in the Jira issue view.
	 */
	issueRequestTypeIds?: number[];

	/**
	 * The IDs of issue types that this form can create issues for.
	 * Forms with IDs populated here will be listed in the create issue form popup.
	 */
	newIssueIssueTypeIds?: number[];

	/**
	 * The IDs of request types that this form can create issues for.
	 * Forms with IDs populated here will be listed in the create issue form popup.
	 */
	newIssueRequestTypeIds?: number[];

	/**
	 * Whether forms created by the create issue form feature should be submitted immediately.
	 * The default is true, which means the form will be submitted at the same time as the issue is created.
	 * If set to false the form will be not be submitted, instead it will be added to the issue with an open status.
	 */
	submitOnCreate: boolean;

	/**
	 * Whether the form is validated before the issue form feature create.
	 * The default is the `submitOnCreate` value, which means by default a form must be valid before the issue form feature create is permitted to to proceed.
	 * If the `submitOnCreate` value is false then by default `validateOnCreate` is also false, but it may be changed to true.
	 *
	 * Note that it is invalid for `validateOnCreate` to be false when `submitOnCreate` value is true. In this case Proforma should automatically
	 * override `validateOnCreate` value to be true. (This will be done on the backend, the UI should not need to do this.)
	 */
	validateOnCreate: boolean;
}

/**
 * Configures how this form is published on the Jira Service Desk portal.
 * Used only on template forms, not available on forms.
 */
export interface FormPublishingPortal {
	/**
	 * The IDs of request types that this form will appear on in the JSD portal.
	 * Forms with IDs populated here will be listed on the JSD portal.
	 */
	portalRequestTypeIds?: number[];

	/**
	 * Whether forms created by the JSD portal should be submitted immediately.
	 * The default is true, which means the form will be submitted immediately after the portal request is created.
	 * If set to false the form will be not be submitted, instead it will be added to the request with an open status.
	 */
	submitOnCreate: boolean;

	/**
	 * Whether the form is validated before the portal create.
	 * The default is the `submitOnCreate` value, which means by default a form must be valid before the portal create is permitted to to proceed.
	 * If the `submitOnCreate` value is false then by default `validateOnCreate` is also false, but it may be changed to true.
	 *
	 * Note that it is invalid for `validateOnCreate` to be false when `submitOnCreate` value is true. In this case Proforma should automatically
	 * override `validateOnCreate` value to be true. (This will be done on the backend, the UI should not need to do this.)
	 */
	validateOnCreate: boolean;
}

/**
 * The design of the form: the static parts that don't change when filled in such as questions, sections and layout.
 */
export interface FormDesign {
	/**
	 * General form settings, ie those which affect the entire form.
	 * Other settings are applied at the section or question level.
	 */
	settings: FormSettings;

	/**
	 * Visual layout of the form, ie a document containing the structure and layout of where the sections and questions appear on the form.
	 * This is an ADF (Atlassian Document Format) document.
	 */
	layout: DocNode[];

	/**
	 * Conditions applied to sections, questions and choices which may change the visibility of those items.
	 */
	conditions: FormConditions;

	/**
	 * Sections of the form, used to apply conditions to more of the form than just a single question.
	 * It is valid for a form to have zero sections; indeed it is normal when there are no conditions on the form.
	 */
	sections: FormSections;

	/**
	 * Questions on the form.
	 */
	questions: FormQuestions;
}

/**
 * The state of the form: the dynamic parts that people change when filling in a form.
 * Optional property, it is not available on template forms.
 */
export interface FormState {
	/**
	 * The visibility of a form, which may be internal or external.
	 *
	 * This only has meaning in the context of a form on Jira Service Desk.
	 * Its meaning in other contexts is undefined. Forms should default to using Internal in other contexts because
	 * Internal is more restrictive and so probably a safer choice should a form ever be moved into Jira Service Desk.
	 */
	visibility: FormVisibility;

	/**
	 * The status of a form: open, submitted or locked.
	 */
	status: FormStatus;

	/**
	 * The answers to questions on this form.
	 *
	 * Answers will only be populated when someone fills out a form; if the form has not been filled out then the
	 * answers object may be empty. It is also possible for the answers object to be partially populated: some but
	 * not all answers listed. However there should not a situation where there is an answer which does not have a
	 * matching question in the form questions object.
	 */
	answers: FormAnswers;
}

/**
 * General form settings, ie those which affect the entire form.
 * Other settings are applied at the section or question level.
 */
export interface FormSettings {
	/**
	 * The ID of the template form that this form was created from.
	 * When this form is itself a template form `templateId` should be equal to the `id` of the form.
	 */
	templateId: number;

	/**
	 * The UUID of the template form that this form was created from.
	 * When this form is itself a template form `templateFormUuid` should be equal to the `uuid` of the form.
	 */
	templateFormUuid?: string;

	/**
	 * The name of this form, set by the form designer.
	 */
	name: string;

	/**
	 * Settings overriding behaviour on the JSD portal
	 */
	portal?: FormSettingsPortal;

	/**
	 * Settings controlling what happens on submit of this form.
	 */
	submit: FormSettingsSubmit;

	/**
	 * Optional language of this form. The language set here defines what language to use for the UI on the form
	 * such as buttons and validation messages. It does not affect the text of the questions or answers which
	 * could be written by users in any language (or even none!) - this is instead determined by `primaryLocale`
	 * and `translatedLocale`, if present.
	 *
	 * If not provided then the language will be determined automatically based on the user's language settings.
	 */
	language?: string;

	/**
	 * The locale that the associated template form's text content is originally written in.
	 */
	primaryLocale?: string;

	/**
	 * The locale that the form's text content has been translated into.
	 *
	 * Only set on `Form`s, and only if a `primaryLocale` is also defined.
	 */
	translatedLocale?: string;
}

/**
 * Settings specific to the Jira Service Desk portal. Not applicable when JSD is not installed.
 */
export interface FormSettingsPortal {
	/**
	 * Whether users on the JSD portal are allowed to submit this form, if it is open and they have access to it on the portal (it is external).
	 * True by default: users are allowed to submit open forms on the portal.
	 */
	canSubmit: boolean;
}

/**
 * Settings controlling what happens on submit of this form.
 */
export interface FormSettingsSubmit {
	/**
	 * Whether to lock the form when it is submitted.
	 * The default is false, meaning the form is submitted but it can be reopened later.
	 * If set to true the form is locked when submitted, meaning it cannot be reopened later (unless overridden by an administrator).
	 */
	lock: boolean;

	/**
	 * Whether to generate a PDF of the form when it is submitted.
	 * The default is false, meaning no PDF is generated by default.
	 */
	pdf: boolean;
}

/**
 * Conditions applied to sections, questions and choices which may change the visibility of those items.
 */
export interface FormConditions {
	[conditionId: string]: FormCondition;
}

/**
 * Base interface for a condition. Different types of conditions extend this to provide type-specific configuration
 * of the condition.
 */
export interface FormCondition {
	t: FormConditionType;
	i: FormConditionInput;
	o: FormConditionOutput;
}

/**
 * The input into a condition which defines whether or not the condition is triggered.
 */
export interface FormConditionInput {
	/**
	 * @private
	 * @deprecated old implementation
	 */
	co?: OneChoiceInput;
	operator?: FormSectionConditionOperator;
	/**
	 * New implementation
	 */
	groups?: FormSectionConditionCheckGroup[];
}

/**
 * A form condition input which is fulfilled when the answer to a choice question includes any one of the choices nominated in `cIds`.
 */
export interface OneChoiceInput {
	cIds: {
		[questionId: FieldId]: OptionId[];
	};
}

/**
 * Condition output describes what items the condition affects when it is triggered: choices, questions, sections or the whole form.
 * Which items may be affected will depend on the condition type.
 */
export interface FormConditionOutput {
	/**
	 * A list of section IDs that this condition applies to.
	 */
	sIds?: string[];

	/**
	 * A map of choice IDs that this condition applies to. Choice IDs are meaningless without a question ID for context so all choice IDs
	 * are specified as the value on a map with question IDs as the key.
	 */
	cIds?: {
		[questionId: FieldId]: OptionId[];
	};

	/**
	 * A boolean indicating whether the condition applies to the entire form.
	 */
	form?: boolean;
}

/**
 * Sections of the form, used to apply conditions to more of the form than just a single question.
 */
export interface FormSections {
	[sectionId: string]: FormSection;
}

/**
 * A single section of a form.
 */
export interface FormSection {
	/**
	 * The type of this section. Set to `Block` by default.
	 */
	type: FormSectionType;

	/**
	 * The name of this section, used for identification but not displayed to people filling in the form.
	 */
	name?: string;

	/**
	 * List of Condition IDs that are applied to this section, this includes cascading conditions for other sections
	 */
	conditions?: string[];
}

/**
 * Questions on the form.
 */
export interface FormQuestions {
	[questionId: FieldId]: FormQuestion;
}

/**
 * A single question on a form.
 */
export interface FormQuestion {
	/**
	 * Type of question: defines how the question appears and is used on the form, and how it can be answered.
	 */
	type: FormQuestionType;

	/**
	 * The label displayed to people filling out the form. Though it is required, it may be an empty string.
	 */
	label: string;

	/**
	 * An optional description displayed to people filling out the form. Intended for help text or other information
	 * associated directly with the question.
	 */
	description?: string;

	/**
	 * Validation rules for the question. It is valid for this to contain no validation rules.
	 * Certain types have implied validation rules which are not explicitly added to `validation`;
	 * for example email questions validate that an answer is a valid email address but do not have store
	 * a specific validator in the form template.
	 */
	validation: FormValidation;

	/**
	 * Choices defined for this question. Only applicable to choice questions.
	 * If a choice question is linked to a Jira field then the choices will be populated from the Jira field
	 * and should not be saved in the form itself while the form is open.
	 */
	choices?: FormChoice[];

	/**
	 * Conditions applicable to this question.
	 */
	conditions?: string[];

	/**
	 * The linked Jira field key, if any. When linked to a Jira field the answer to a question is stored on the Jira field
	 * for the issue rather than in the form directly — while the form is open, it is saved in the form on submit.
	 */
	jiraField?: string;

	/**
	 * The linked data connection id, if any. When linked to a data connection, the available answers are sourced from the
	 * data connection and the answer IDs are stored in the form directly.
	 */
	dcId?: string;

	/**
	 * The default value for this question, if any. If populated then this answer is automatically applied when the form
	 * is filled in.
	 */
	defaultAnswer?: FormAnswer;

	/**
	 * a unique identifier for this question on a form.
	 */
	questionKey?: string;
}

/**
 * A choice on a choice question.
 */
export interface FormChoice {
	id: OptionId;
	label: string;
	cmdbObject?: CmdbObject;
	other?: boolean;
	children?: FormChoice[];
}

/**
 * Form question validation configuration.
 */
export interface FormValidation {
	/** Required */
	rq?: boolean;
	/** Minimum characters */
	mnc?: number;
	/** Minimum words */
	mnw?: number;
	/** Minimum number */
	mnn?: number;
	/** Minimum date */
	mnd?: IsoDate;
	/** Minimum time */
	mnt?: LocalTime;
	/** Minimum number of choice selections */
	mns?: number;
	/** Maximum characters */
	mxc?: number;
	/** Maximum words */
	mxw?: number;
	/** Maximum number */
	mxn?: number;
	/** Maximum date */
	mxd?: IsoDate;
	/** Maximum time */
	mxt?: LocalTime;
	/** Maximum number of choice selections */
	mxs?: number;
	/** Whole numbers only */
	wh?: boolean;
	/** ID of required choice  */
	ch?: OptionId;
	/** Regex format validation */
	rgx?: {
		/** Regex pattern */
		p: string | RegExp;
		/** Message to display to user if the answer doesn't match the expected format */
		m?: string;
	};
}

/**
 * The answers to questions on a form.
 *
 * Answers will only be populated when someone fills out a form; if the form has not been filled out then the
 * answers object may be empty. It is also possible for the answers object to be partially populated: some but
 * not all answers listed. However there should not a situation where there is an answer which does not have a
 * matching question in the form questions object.
 */
export interface FormAnswers {
	[questionId: FieldId]: FormAnswer;
}

/**
 * Answer to a single question on a form.
 * Can also be used to define the default answer to a question.
 */
export interface FormAnswer {
	/**
	 * Text part of an answer.
	 */
	text?: string | null;

	/**
	 * Date part of an answer.
	 */
	date?: IsoDate | null;

	/**
	 * Time part of an answer.
	 */
	time?: LocalTime | null;

	/**
	 * IDs of choices that have been selected. If it is a cascading choice, it will be in the format parentId:childId.
	 */
	choices?: OptionId[];

	/**
	 * Users that have been selected.
	 */
	users?: FormUser[];

	/**
	 * Rich text answer in adf format
	 */
	adf?: DocNode | null;

	/**
	 * Media API file IDs of files uploaded as attachments.
	 */
	files?: { id: string; name?: string }[] | null;
}

/**
 * A user selected in a user picker.
 */
export interface FormUser {
	id: string;
	name: string;
}

/** A string in ISO 8601 date and time format, in UTC timezone. */
export type UtcDateTime = string;

/**
 * The visibility of a form, which may be internal or external.
 */
export enum FormVisibility {
	/**
	 * Internal form: in the context of Jira Service Desk this means the form only appears inside Jira and not on the Jira Service Desk portal.
	 * In other contexts like template forms and Jira Software the meaning is undefined.
	 */
	Internal = 'i',

	/**
	 * External form: in the context of Jira Service Desk this means the form appears both in Jira and on the Jira Service Desk portal.
	 * In other contexts like template forms and Jira Software the meaning is undefined.
	 */
	External = 'e',
}

export enum FormStatus {
	Open = 'o',
	Submitted = 's',
	Locked = 'l',
}

export enum FormConditionType {
	Show = 'sh',
	Hide = 'hide',
}

export enum FormSectionType {
	Block = 'b',
	Page = 'p',
}

export enum FormQuestionType {
	Attachment = 'at',
	ChoiceCascading = 'cc',
	ChoiceDropdown = 'cd',
	ChoiceDropdownMultiple = 'cl',
	ChoiceMultiple = 'cm',
	ChoiceSingle = 'cs',
	CmdbObject = 'ob',
	Date = 'da',
	DateTime = 'dt',
	Number = 'no',
	TextEmail = 'te',
	TextLong = 'tl',
	TextParagraph = 'pg',
	RichTextParagraph = 'rt',
	TextShort = 'ts',
	TextUrl = 'tu',
	Time = 'ti',
	UserMultiple = 'um',
	UserSingle = 'us',
	Rating = 'rg',
	Scale = 'sc',
}

/*
 * CmdbObject types
 */

export interface CmdbAvatar {
	id: string;
	url16: string;
	url48: string;
	mediaClientConfig: CmdbMediaClientConfig;
}

export interface CmdbMediaClientConfig {
	fileId: string;
	clientId: string;
	mediaBaseUrl: string;
	mediaJwtToken: string;
}

export interface ObjectTypeAttribute {
	type: number;
	name: string;
	label: boolean;
}

export interface CmdbObjectAttribute {
	id: string;
	objectTypeAttribute: ObjectTypeAttribute;
	objectAttributeValues: ObjectAttributeValue[];
}

export interface ObjectAttributeValue {
	displayValue: string;
}

export interface CmdbObject {
	id: string;
	label: string;
	avatar: CmdbAvatar;
	attributes: CmdbObjectAttribute[];
}

// oxlint-disable-next-line eslint/no-redeclare
export const FormRestrictionStatus = {
	OPEN: 'open',
	RESTRICTED: 'restricted',
} as const;
export type FormRestrictionStatus =
	(typeof FormRestrictionStatus)[keyof typeof FormRestrictionStatus];
